import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import FilterSidebarFavoriteButton from "@/modules/hub/features/ResourceLibrary/components/FilterSidebar/FavoriteButton/FilterSidebarFavoriteButton";
import FilterSidebarSharedWithMeButton from "@/modules/hub/features/ResourceLibrary/components/FilterSidebar/SharedWithMeButton/FilterSidebarSharedWithMeButton";
import { ResourceLibraryMetadataFilters } from "@/modules/hub/features/ResourceLibrary/api/getResourceLibraryMetadata/types";
import { Filter as DefaultFilterIcon } from "lucide-react";
import { FilterSidebarFormData } from "@/modules/hub/features/ResourceLibrary/hooks/FilterSidebarFormSchema";
import { IconSvg } from "@/modules/hub/features/ResourceLibrary/components/FilterSidebar/IconSvg/IconSvg";
import { getTradeBadgeClassByName } from "@/modules/hub/features/ResourceLibrary/utils/tradeStyling";

interface FilterSidebarFormProps {
  loading?: boolean;
  onSubmit: (values: FilterSidebarFormData) => void;
  form: UseFormReturn<FilterSidebarFormData>;
  filters: ResourceLibraryMetadataFilters | null;
  showProcessWheel?: boolean;
  showJobTitle?: boolean;
}

const FilterSidebarForm: React.FC<FilterSidebarFormProps> = ({
  onSubmit,
  form,
  filters,
  showProcessWheel = false,
  showJobTitle = true,
}) => {
  const { handleSubmit, control } = form;

  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllProcessWheels, setShowAllProcessWheels] = useState(false);

  const availableTrades = filters?.trades ?? [];
  const availableContentTypes = (filters?.resourceTypes ?? []).filter(
    (type) => type.name !== "Podcast" && type.name !== "News",
  );
  const availableCategories = filters?.resourceCategories ?? [];

  const visibleCategories = showAllCategories
    ? availableCategories
    : availableCategories.slice(0, 3);

  const availableProcessWheels = filters?.processWheels ?? [];

  const visibleProcessWheels = showAllProcessWheels
    ? availableProcessWheels
    : availableProcessWheels.slice(0, 3);

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <FormField
          control={control}
          name="isFavoriteOnly"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Favorite</FormLabel>
              <FormControl>
                <FilterSidebarFavoriteButton
                  onChange={field.onChange}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="isSharedWithMeOnly"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Shared</FormLabel>
              <FormControl>
                <FilterSidebarSharedWithMeButton
                  onChange={field.onChange}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="contentTypes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content Types</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  {availableContentTypes.map((type) => {
                    const isSelected = field.value?.some(
                      (item) => item.id === type.id,
                    );

                    const icon = type.icon ? (
                      <IconSvg
                        className="w-4 h-4 text-gray-600 dark:text-slate-400 [&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-current [&>svg]:fill-none"
                        svg={type.icon}
                      />
                    ) : (
                      <DefaultFilterIcon className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                    );

                    return (
                      <button
                        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                          isSelected
                            ? "bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60"
                            : "bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700"
                        }`}
                        key={type.id}
                        onClick={() => {
                          const selected = [...(field.value || [])];
                          const index = selected.findIndex(
                            (item) => item.id === type.id,
                          );

                          if (index >= 0) {
                            selected.splice(index, 1);
                          } else {
                            selected.push({
                              id: type.id,
                              name: type.name,
                              resourceCount: type.resourceCount,
                            });
                          }

                          field.onChange(selected);
                        }}
                        type="button"
                      >
                        <div className="flex items-center gap-2">
                          {icon}
                          <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                            {type.name}
                          </span>
                        </div>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            isSelected
                              ? "bg-blue-500 text-white"
                              : "bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-400"
                          }`}
                        >
                          {type.resourceCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="trades"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Trades</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  {availableTrades.map((trade) => {
                    const isSelected = field.value?.some(
                      (item) => item.id === trade.id,
                    );

                    const selectedBadgeClass = getTradeBadgeClassByName(
                      trade.name,
                    );

                    const icon = trade.icon ? (
                      <IconSvg
                        className="w-4 h-4 text-gray-600 dark:text-slate-400 [&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-current [&>svg]:fill-none"
                        svg={trade.icon}
                      />
                    ) : (
                      <DefaultFilterIcon className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                    );

                    return (
                      <button
                        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                          isSelected
                            ? "bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60"
                            : "bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700"
                        }`}
                        key={trade.id}
                        onClick={() => {
                          const selected = [...(field.value || [])];
                          const index = selected.findIndex(
                            (item) => item.id === trade.id,
                          );

                          if (index >= 0) {
                            selected.splice(index, 1);
                          } else {
                            selected.push({
                              id: trade.id,
                              name: trade.name,
                            });
                          }

                          field.onChange(selected);
                        }}
                        type="button"
                      >
                        <div className="flex items-center gap-2">
                          {icon}
                          <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                            {trade.name}
                          </span>
                        </div>
                        {trade.count !== undefined && (
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                              isSelected
                                ? selectedBadgeClass
                                : "bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-400"
                            }`}
                          >
                            {trade.count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {showJobTitle && (
          <FormField
            control={control}
            name="businessRoles"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Job Title</FormLabel>
                <FormControl>
                  <div className="space-y-2">
                    {(filters?.businessRoles ?? []).map((role) => {
                      const isSelected = field.value?.some(
                        (item) => item.id === role.id,
                      );

                      return (
                        <label
                          className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300"
                          key={role.id}
                        >
                          <input
                            checked={isSelected}
                            className="accent-primary h-4 w-4"
                            onChange={(e) => {
                              const { checked } = e.target;
                              const currentValue = [...(field.value || [])];

                              if (checked) {
                                if (!isSelected) {
                                  currentValue.push({
                                    id: role.id,
                                    name: role.name,
                                    description: role.description,
                                  });
                                }
                              } else {
                                const index = currentValue.findIndex(
                                  (item) => item.id === role.id,
                                );
                                if (index >= 0) {
                                  currentValue.splice(index, 1);
                                }
                              }

                              field.onChange(currentValue);
                            }}
                            type="checkbox"
                          />
                          <span className="flex-1">{role.name}</span>
                          {role.count !== undefined && (
                            <span className="text-xs text-gray-500 dark:text-slate-500">
                              ({role.count})
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={control}
          name="categories"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Departments</FormLabel>
              <FormControl>
                <>
                  <div className="space-y-2">
                    {visibleCategories.map((cat) => {
                      const isSelected = field.value?.some(
                        (item) => item.id === cat.id,
                      );

                      return (
                        <label
                          className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300"
                          key={cat.id}
                        >
                          <input
                            checked={isSelected}
                            className="accent-primary h-4 w-4"
                            onChange={(e) => {
                              const { checked } = e.target;
                              const currentValue = [...(field.value || [])];

                              if (checked && !isSelected) {
                                currentValue.push({
                                  id: cat.id,
                                  name: cat.name,
                                });
                              } else if (!checked) {
                                const index = currentValue.findIndex(
                                  (item) => item.id === cat.id,
                                );
                                if (index >= 0) {
                                  currentValue.splice(index, 1);
                                }
                              }

                              field.onChange(currentValue);
                            }}
                            type="checkbox"
                          />
                          <span className="flex-1">{cat.name}</span>
                          {cat.count !== undefined && (
                            <span className="text-xs text-gray-500 dark:text-slate-500">
                              ({cat.count})
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {availableCategories.length > 3 && (
                    <div className="mt-2">
                      <button
                        className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
                        onClick={() => setShowAllCategories((prev) => !prev)}
                        type="button"
                      >
                        {showAllCategories ? "View less" : "View more"}
                      </button>
                    </div>
                  )}
                </>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {showProcessWheel && availableProcessWheels.length > 0 && (
          <FormField
            control={control}
            name="processWheels"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Process Wheel</FormLabel>
                <FormControl>
                  <>
                    <div className="space-y-2">
                      {visibleProcessWheels.map((pw) => {
                        const isSelected = field.value?.some(
                          (item) => item.id === pw.id,
                        );

                        return (
                          <label
                            className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300"
                            key={pw.id}
                          >
                            <input
                              checked={isSelected}
                              className="accent-primary h-4 w-4"
                              onChange={(e) => {
                                field.onChange(
                                  e.target.checked
                                    ? [{ id: pw.id, name: pw.name }]
                                    : [],
                                );
                              }}
                              type="checkbox"
                            />
                            <span className="flex-1">{pw.name}</span>
                            {pw.count !== undefined && (
                              <span className="text-xs text-gray-500 dark:text-slate-500">
                                ({pw.count})
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>

                    {availableProcessWheels.length > 3 && (
                      <div className="mt-2">
                        <button
                          className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
                          onClick={() =>
                            setShowAllProcessWheels((prev) => !prev)
                          }
                          type="button"
                        >
                          {showAllProcessWheels ? "View less" : "View more"}
                        </button>
                      </div>
                    )}
                  </>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  );
};

export default FilterSidebarForm;
